import { doc, runTransaction } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import type { PortalLinkDoc } from '../types/firestore';
import type { PortalChangeOrder, PortalPayment, PortalEstimate } from '../types/domain';

// ── Doc path helpers ──────────────────────────────────────────────────────────
// Firestore layout: shared/{docId} where docId = portallink__<token>.
// Verified by reading portal.ts createPortalLink (uses sharedDoc(docId)) and
// portalSync.ts syncPortalWithProject (uses getShared/setShared with the same
// pattern). sharedDoc() resolves to doc(db, 'shared', docId) per
// lib/firebase/firestore.ts.

export const portalLinkDocId = (token: string) => `portallink__${token}`;
export const portalLinkDocPath = (token: string) => `shared/portallink__${token}`;

// ── Pure math helpers ─────────────────────────────────────────────────────────

export function computeContractTotal(
  estimate: PortalEstimate | undefined,
  cos: readonly PortalChangeOrder[],
): number {
  const est = estimate?.total ?? 0;
  const approved = cos.reduce((sum, c) => sum + (c.approvalStatus === 'approved' ? c.amount : 0), 0);
  return est + approved;
}

export function computePaymentsToDate(pays: readonly PortalPayment[]): number {
  return pays.reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function computeBalanceDue(
  estimate: PortalEstimate | undefined,
  cos: readonly PortalChangeOrder[],
  pays: readonly PortalPayment[],
): number {
  const due = computeContractTotal(estimate, cos) - computePaymentsToDate(pays);
  return due < 0 ? 0 : due;
}

// ── Pure array mutation helper ────────────────────────────────────────────────

export function updateCOByIdPure(
  list: readonly PortalChangeOrder[],
  id: string,
  mutator: (co: PortalChangeOrder) => PortalChangeOrder,
): PortalChangeOrder[] {
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return list as PortalChangeOrder[];
  const next = list.slice();
  next[idx] = mutator(list[idx]!);
  return next;
}

// ── Transactional Firestore wrappers ─────────────────────────────────────────
// Each wrapper uses runTransaction to read the whole portallink doc, mutate
// the relevant array field, and write it back atomically — the only safe
// primitive Firestore gives for array-element edits.

export async function updateCOById(
  db: Firestore,
  token: string,
  coId: string,
  mutator: (co: PortalChangeOrder) => PortalChangeOrder,
): Promise<void> {
  const ref = doc(db, 'shared', portalLinkDocId(token));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`portallink not found: ${token}`);
    const data = snap.data() as PortalLinkDoc;
    const nextCos = updateCOByIdPure(data.portalChangeOrders ?? [], coId, mutator);
    if (nextCos === data.portalChangeOrders) return;
    tx.update(ref, { portalChangeOrders: nextCos, updatedAt: Date.now() });
  });
}

export async function appendPayment(
  db: Firestore,
  token: string,
  payment: PortalPayment,
): Promise<void> {
  const ref = doc(db, 'shared', portalLinkDocId(token));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`portallink not found: ${token}`);
    const data = snap.data() as PortalLinkDoc;
    const next = [...(data.portalPayments ?? []), payment];
    tx.update(ref, { portalPayments: next, updatedAt: Date.now() });
  });
}

export async function appendChangeOrder(
  db: Firestore,
  token: string,
  co: PortalChangeOrder,
): Promise<void> {
  const ref = doc(db, 'shared', portalLinkDocId(token));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`portallink not found: ${token}`);
    const data = snap.data() as PortalLinkDoc;
    const next = [...(data.portalChangeOrders ?? []), co];
    tx.update(ref, { portalChangeOrders: next, updatedAt: Date.now() });
  });
}

export async function setPortalEstimate(
  db: Firestore,
  token: string,
  estimate: PortalEstimate,
): Promise<void> {
  const ref = doc(db, 'shared', portalLinkDocId(token));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`portallink not found: ${token}`);
    tx.update(ref, { portalEstimate: estimate, updatedAt: Date.now() });
  });
}
